import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { isValidZip, isValidEmail, isValidPhone, isValidUrl, filterNameInput, filterPhoneInput, filterDigitsInput, filterZipInput, hasAlphanumeric } from "@/lib/validation";
import {
  HeartHandshake, Building2, Brain, Activity, Heart, Baby, Shield, Moon,
  Clock, Flag, UserCheck, Stethoscope, Smartphone, Mail, Info, ShieldCheck,
  CheckCircle2, Loader2, MapPin, Star, Bell, ChevronLeft, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { BAAReviewPanel } from "@/components/BAAReviewPanel";
import { BAASignaturePad, type SignatureResult } from "@/components/BAASignaturePad";
import { BAA_VERSION, computeBAADocumentHash, getBAAPlainText } from "@/lib/legal/baa-content";
import { HC_SPECIALTIES, GH_SPECIALTIES } from "@/lib/specialties";

interface Props { onComplete: () => void; }

type Segment = "homecare" | "grouphome";

interface Profile {
  agencyName: string; ownerName: string; ownerPhone: string; ownerEmail: string;
  bookingLink: string; serviceZips: string[]; goldenZips: string[];
  specialties: string[]; prioritySpecialties: string[]; payerTypes: string[];
  notifySms: boolean; notifyEmail: boolean; senderName: string; replyToEmail: string;
  programType: string[]; localities: string[]; primaryLocalities: string[];
  waiverTypes: string[]; bedCount: string; licenseNumber: string; dbhdsRegion: string;
}

// Max lengths enforced client-side (via <input maxLength>) and mirrored by
// CHECK constraints on public.profiles server-side — keep both in sync.
const MAX_LENGTHS = {
  agencyName: 200,
  ownerName: 100,
  ownerPhone: 20,
  ownerEmail: 255,
  bookingLink: 500,
  licenseNumber: 50,
  senderName: 100,
  replyToEmail: 255,
  signerTitle: 100,
  zip: 5,
  locality: 100,
} as const;

const HC_PAYERS = ["Private Pay", "Medicare", "Private Insurance", "Long-Term Care Insurance", "Medicaid Waiver", "Veterans Benefits"];
const GH_WAIVERS = ["DD Waiver", "MH Waiver", "CCC+ Waiver", "Private Pay"];
const DBHDS_REGIONS = ["Northern Virginia", "Western Virginia", "Central Virginia", "Eastern Virginia", "Southwest Virginia", "Valley"];

export default function OnboardingWizard({ onComplete }: Props) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [segment, setSegment] = useState<Segment | null>(null);
  const [direction, setDirection] = useState<"forward" | "back">("forward");
  const [profile, setProfile] = useState<Profile>({
    agencyName: "", ownerName: "", ownerPhone: "", ownerEmail: "", bookingLink: "",
    serviceZips: [], goldenZips: [], specialties: [], prioritySpecialties: [],
    payerTypes: [], notifySms: true, notifyEmail: true, senderName: "", replyToEmail: "",
    programType: [], localities: [], primaryLocalities: [], waiverTypes: [],
    bedCount: "", licenseNumber: "", dbhdsRegion: "",
  });
  const [zipInput, setZipInput] = useState("");
  const [zipError, setZipError] = useState("");
  const [localityInput, setLocalityInput] = useState("");
  const [localityError, setLocalityError] = useState("");
  const [hipaaAcknowledged, setHipaaAcknowledged] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [revealLines, setRevealLines] = useState<number[]>([]);

  // BAA e-signature state
  const [baaScrolledEnd, setBaaScrolledEnd] = useState(false);
  const [baaSignerTitle, setBaaSignerTitle] = useState("");
  const [baaSignature, setBaaSignature] = useState<SignatureResult | null>(null);
  const [baaAttestAuthority, setBaaAttestAuthority] = useState(false);
  const [baaAttestEsign, setBaaAttestEsign] = useState(false);
  const [baaSigning, setBaaSigning] = useState(false);

  // Inject Inter font
  useEffect(() => {
    if (!document.querySelector('link[href*="fonts.googleapis.com/css2?family=Inter"]')) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  const updateProfile = useCallback((key: keyof Profile, value: string | string[] | boolean) => {
    setProfile(prev => ({ ...prev, [key]: value }));
  }, []);

  const toggleArrayItem = useCallback((key: keyof Profile, value: string) => {
    setProfile(prev => {
      const arr = prev[key] as string[];
      return { ...prev, [key]: arr.includes(value) ? arr.filter(x => x !== value) : [...arr, value] };
    });
  }, []);

  function handleNext() { setDirection("forward"); setStep(s => s + 1); }
  function handleBack() { setDirection("back"); setStep(s => Math.max(1, s - 1)); }

  function handleSelectSegment(s: Segment) {
    setSegment(s);
    setTimeout(() => { setDirection("forward"); setStep(2); }, 300);
  }

  function addZip() {
    const z = zipInput.trim();
    if (!isValidZip(z)) { setZipError("Please enter a valid 5-digit ZIP code."); return; }
    if (profile.serviceZips.includes(z)) { setZipError("ZIP already added."); return; }
    updateProfile("serviceZips", [...profile.serviceZips, z]);
    setZipInput(""); setZipError("");
  }

  function addLocality() {
    const l = localityInput.trim();
    if (!l) return;
    if (l.length > MAX_LENGTHS.locality) {
      setLocalityError(`Locality must be ${MAX_LENGTHS.locality} characters or fewer.`);
      return;
    }
    if (profile.localities.includes(l)) return;
    updateProfile("localities", [...profile.localities, l]);
    setLocalityInput("");
    setLocalityError("");
  }

  async function handleSave() {
    if (!user) return;

    // Advance to step 7 immediately so the user sees the loader (was missing before).
    setDirection("forward");
    setStep(7);
    setSaving(true);
    setRevealLines([]);

    // Stagger loading lines
    setTimeout(() => setRevealLines(p => [...p, 1]), 400);
    setTimeout(() => setRevealLines(p => [...p, 2]), 1000);
    setTimeout(() => setRevealLines(p => [...p, 3]), 1600);

    const { error } = await supabase.from("profiles").update({
      segment,
      agency_name: profile.agencyName,
      owner_name: profile.ownerName,
      owner_phone: profile.ownerPhone,
      agency_email: profile.ownerEmail,
      booking_link: profile.bookingLink,
      service_zips: profile.serviceZips,
      golden_zips: profile.goldenZips,
      service_specialties: profile.specialties,
      priority_specialties: profile.prioritySpecialties,
      ideal_client_payer_sources: profile.payerTypes,
      notify_sms: profile.notifySms,
      notify_email: profile.notifyEmail,
      sender_display_name: profile.senderName || profile.agencyName,
      reply_to_email: profile.replyToEmail || profile.ownerEmail,
      localities: profile.localities,
      primary_localities: profile.primaryLocalities,
      waiver_types: profile.waiverTypes,
      bed_count: profile.bedCount ? parseInt(profile.bedCount) : null,
      license_number: profile.licenseNumber || null,
      dbhds_region: profile.dbhdsRegion || null,
    } as any).eq("id", user.id);

    if (error) {
      toast.error("Failed to save: " + error.message);
      setSaving(false);
      setStep(6); // roll back so the user can retry
      return;
    }

    setTimeout(() => { setDone(true); }, 2200);
  }

  /**
   * Handles the BAA e-signature flow, then continues with the regular profile
   * save (which advances to Step 7).
   */
  async function handleSignAndComplete() {
    if (!user) return;
    if (!baaSignature) {
      toast.error("Please provide a signature.");
      // Record the failed attempt so it appears in the audit trail.
      try {
        await supabase.from("baa_signing_attempts").insert({
          user_id: user.id,
          status: "failure",
          failure_reason: "missing_signature",
          signer_name: profile.ownerName?.trim() || null,
          signer_email: user.email ?? null,
          document_version: BAA_VERSION,
          user_agent: navigator.userAgent,
        } as any);
      } catch { /* best-effort */ }
      return;
    }
    setBaaSigning(true);
    try {
      const document_hash = await computeBAADocumentHash();
      const { error } = await supabase.functions.invoke("generate-signed-baa", {
        body: {
          signer_name: profile.ownerName.trim(),
          signer_title: baaSignerTitle.trim(),
          signature_type: baaSignature.type,
          signature_data: baaSignature.dataUrl,
          document_version: BAA_VERSION,
          document_hash,
          baa_text: getBAAPlainText(),
        },
      });
      if (error) throw error;
      // Mark hipaa acknowledgement true since we now have a signed record.
      setHipaaAcknowledged(true);
      toast.success("BAA signed successfully");
      await handleSave();
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || "Failed to sign BAA. Please try again.");
    } finally {
      setBaaSigning(false);
    }
  }

  const progressPercent = step > 1 && step <= 7 ? ((step - 1) / 6) * 100 : 0;

  const animClass = direction === "forward"
    ? "animate-[slideInRight_280ms_ease-out]"
    : "animate-[slideInLeft_280ms_ease-out]";

  return (
    <div className="flex min-h-screen items-center justify-center p-4" style={{ background: "#F8F9FB", fontFamily: "'Inter', sans-serif" }}>
      <style>{`
        @keyframes slideInRight { from { opacity:0; transform:translateX(24px); } to { opacity:1; transform:translateX(0); } }
        @keyframes slideInLeft { from { opacity:0; transform:translateX(-24px); } to { opacity:1; transform:translateX(0); } }
      `}</style>

      <div className="w-full max-w-[680px] rounded-2xl bg-white p-7 sm:p-12" style={{ boxShadow: "0 8px 40px rgba(0,0,0,0.10)" }}>
        {/* Progress bar for steps 2-6 */}
        {step >= 2 && step <= 6 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-lg font-bold" style={{ color: "#1F3864" }}>LegacyGrowth Engine</span>
              <span className="text-xs" style={{ color: "#999" }}>Step {step - 1} of 6</span>
            </div>
            <div className="h-1.5 w-full rounded-full" style={{ background: "#E8ECF0" }}>
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progressPercent}%`, background: "#D4A843" }} />
            </div>
          </div>
        )}

        {/* Back button for steps 2-6 */}
        {step >= 2 && step <= 6 && (
          <button onClick={handleBack} className="flex items-center gap-1 text-sm mb-4 hover:opacity-70 transition" style={{ color: "#888" }}>
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
        )}

        <div key={step} className={animClass}>
          {step === 1 && <Step1 segment={segment} onSelect={handleSelectSegment} />}
          {step === 2 && segment && <Step2 segment={segment} profile={profile} updateProfile={updateProfile} onNext={handleNext} />}
          {step === 3 && segment && (
            <Step3
              segment={segment} profile={profile} updateProfile={updateProfile} toggleArrayItem={toggleArrayItem}
              zipInput={zipInput} setZipInput={setZipInput} zipError={zipError} addZip={addZip}
              localityInput={localityInput} setLocalityInput={setLocalityInput} localityError={localityError} addLocality={addLocality}
              onNext={handleNext}
            />
          )}
          {step === 4 && segment && (
            <Step4 segment={segment} profile={profile} toggleArrayItem={toggleArrayItem} onNext={handleNext} />
          )}
          {step === 5 && segment && (
            <Step5 segment={segment} profile={profile} updateProfile={updateProfile} onNext={handleNext} />
          )}
          {step === 6 && (
            <Step6
              ownerName={profile.ownerName}
              agencyName={profile.agencyName}
              scrolledEnd={baaScrolledEnd}
              onScrolledEnd={() => setBaaScrolledEnd(true)}
              signerTitle={baaSignerTitle}
              setSignerTitle={setBaaSignerTitle}
              signature={baaSignature}
              setSignature={setBaaSignature}
              attestAuthority={baaAttestAuthority}
              setAttestAuthority={setBaaAttestAuthority}
              attestEsign={baaAttestEsign}
              setAttestEsign={setBaaAttestEsign}
              signing={baaSigning}
              onSign={handleSignAndComplete}
            />
          )}
          {step === 7 && (
            <Step7 saving={saving} done={done} segment={segment} profile={profile} revealLines={revealLines} onComplete={onComplete} />
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── STEP 1: SEGMENT SELECTOR ─── */
function Step1({ segment, onSelect }: { segment: Segment | null; onSelect: (s: Segment) => void }) {
  return (
    <div className="text-center">
      <p className="text-lg font-bold mb-2" style={{ color: "#1F3864" }}>LegacyGrowth Engine</p>
      <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-4" style={{ background: "#FFF8E7", color: "#D4A843" }}>WELCOME</span>
      <h1 className="text-3xl sm:text-4xl font-bold mb-3" style={{ color: "#1F3864", lineHeight: 1.3 }}>Let's personalize your LegacyGrowth Engine.</h1>
      <p className="text-base mb-8 mx-auto max-w-[480px]" style={{ color: "#666" }}>
        First, tell us what type of organization you run. We'll set up your scoring engine, templates, and dashboard to match your world exactly.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <SegmentCard
          selected={segment === "homecare"}
          onClick={() => onSelect("homecare")}
          icon={<HeartHandshake className="w-10 h-10" style={{ color: "#D4A843" }} />}
          title="Home Care Agency"
          desc="I provide non-medical personal care, companion care, or skilled home health services to clients in their homes."
          pills={["Private Pay", "Medicare", "Private Insurance", "Medicaid Waiver"]}
        />
        <SegmentCard
          selected={segment === "grouphome"}
          onClick={() => onSelect("grouphome")}
          icon={<Building2 className="w-10 h-10" style={{ color: "#D4A843" }} />}
          title="Group Home / Residential Program"
          desc="I operate a DBHDS/DDA licensed residential program serving individuals with intellectual disabilities, mental health needs, or both."
          pills={["DD Waiver", "MH Waiver", "DBHDS/DDA Licensed"]}
        />
      </div>
      <p className="text-sm" style={{ color: "#aaa" }}>You can update this later in Settings.</p>
    </div>
  );
}

function SegmentCard({ selected, onClick, icon, title, desc, pills }: {
  selected: boolean; onClick: () => void; icon: React.ReactNode; title: string; desc: string; pills: string[];
}) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center text-center p-8 rounded-xl transition-all cursor-pointer"
      style={{
        border: selected ? "2px solid #D4A843" : "2px solid #e5e7eb",
        boxShadow: selected ? "0 0 0 4px rgba(212,168,67,0.15)" : "0 2px 8px rgba(0,0,0,0.04)",
        background: "#fff",
      }}
    >
      {icon}
      <h3 className="text-lg font-bold mt-3 mb-2" style={{ color: "#1F3864" }}>{title}</h3>
      <p className="text-sm mb-3" style={{ color: "#666" }}>{desc}</p>
      <div className="flex flex-wrap justify-center gap-1.5">
        {pills.map(p => (
          <span key={p} className="px-2 py-0.5 rounded text-xs" style={{ background: "#f3f4f6", color: "#888" }}>{p}</span>
        ))}
      </div>
    </button>
  );
}

/* ─── STEP 2: ORGANIZATION PROFILE ─── */
function Step2({ segment, profile, updateProfile, onNext }: {
  segment: Segment; profile: Profile; updateProfile: (k: keyof Profile, v: any) => void; onNext: () => void;
}) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const canContinue = profile.agencyName && profile.ownerName && profile.ownerPhone && profile.ownerEmail
    && (segment !== "grouphome" || profile.dbhdsRegion);

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (profile.agencyName.length > MAX_LENGTHS.agencyName) errs.agencyName = `Organization Name must be ${MAX_LENGTHS.agencyName} characters or fewer.`;
    else if (profile.agencyName && !hasAlphanumeric(profile.agencyName)) errs.agencyName = "Organization Name must contain at least one letter or number.";
    if (profile.ownerName.length > MAX_LENGTHS.ownerName) errs.ownerName = `Your Full Name must be ${MAX_LENGTHS.ownerName} characters or fewer.`;
    else if (profile.ownerName && !hasAlphanumeric(profile.ownerName)) errs.ownerName = "Your Full Name must contain at least one letter or number.";
    if (profile.ownerPhone.length > MAX_LENGTHS.ownerPhone) errs.ownerPhone = `Phone number must be ${MAX_LENGTHS.ownerPhone} characters or fewer.`;
    else if (profile.ownerPhone && !isValidPhone(profile.ownerPhone)) errs.ownerPhone = "Enter a valid phone number.";
    if (!isValidEmail(profile.ownerEmail.trim())) errs.ownerEmail = "Enter a valid email address";
    else if (profile.ownerEmail.length > MAX_LENGTHS.ownerEmail) errs.ownerEmail = `Email must be ${MAX_LENGTHS.ownerEmail} characters or fewer.`;
    if (profile.bookingLink.length > MAX_LENGTHS.bookingLink) errs.bookingLink = `Booking link must be ${MAX_LENGTHS.bookingLink} characters or fewer.`;
    else if (profile.bookingLink && !isValidUrl(profile.bookingLink)) errs.bookingLink = "Enter a valid URL, including https:// (e.g. https://calendly.com/yourname).";
    if (segment === "grouphome" && profile.licenseNumber.length > MAX_LENGTHS.licenseNumber) {
      errs.licenseNumber = `License number must be ${MAX_LENGTHS.licenseNumber} characters or fewer.`;
    }
    if (segment === "grouphome" && !profile.dbhdsRegion) {
      errs.dbhdsRegion = "Select a DBHDS Region.";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleContinue() {
    if (!validate()) return;
    onNext();
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-1" style={{ color: "#1F3864" }}>
        {segment === "homecare" ? "Tell us about your agency." : "Tell us about your program."}
      </h2>
      <p className="text-sm mb-6" style={{ color: "#888" }}>This information personalizes your AI intake coordinator, email templates, and dashboard.</p>
      <div className="space-y-4">
        <Field label="Organization Name" placeholder={segment === "homecare" ? "e.g. Sunrise Home Care LLC" : "e.g. Crestview Residential Services LLC"} value={profile.agencyName} onChange={v => updateProfile("agencyName", v)} maxLength={MAX_LENGTHS.agencyName} error={errors.agencyName} />
        <Field label="Your Full Name" placeholder="e.g. Eileen Teckham" value={profile.ownerName} onChange={v => updateProfile("ownerName", filterNameInput(v))} maxLength={MAX_LENGTHS.ownerName} error={errors.ownerName} />
        <Field label="Your Phone Number" placeholder="e.g. (703) 555-0192" type="text" inputMode="tel" value={profile.ownerPhone} onChange={v => updateProfile("ownerPhone", filterPhoneInput(v))} maxLength={MAX_LENGTHS.ownerPhone} error={errors.ownerPhone} />
        <Field label="Your Email Address" placeholder="e.g. eileen@sunrisehomecare.com" type="email" value={profile.ownerEmail} onChange={v => updateProfile("ownerEmail", v)} maxLength={MAX_LENGTHS.ownerEmail} error={errors.ownerEmail} />
        <div>
          <Field label="Discovery Call / Booking Link" placeholder="e.g. https://calendly.com/yourname" type="url" value={profile.bookingLink} onChange={v => updateProfile("bookingLink", v)} maxLength={MAX_LENGTHS.bookingLink} error={errors.bookingLink} />
          <p className="text-xs mt-1" style={{ color: "#aaa" }}>Paste your Calendly, TidyCal, or Google Booking link. This is added to your hot lead alerts.</p>
        </div>
        {segment === "grouphome" && (
          <>
            <Field label="DBHDS License Number" placeholder="e.g. 12345-VA" value={profile.licenseNumber} onChange={v => updateProfile("licenseNumber", v)} maxLength={MAX_LENGTHS.licenseNumber} error={errors.licenseNumber} />
            <div>
              <label className="text-sm font-semibold block mb-1" style={{ color: "#1F3864" }}>DBHDS Region</label>
              <select
                value={profile.dbhdsRegion}
                onChange={e => updateProfile("dbhdsRegion", e.target.value)}
                className="w-full px-4 py-3 rounded-lg border text-sm transition focus:outline-none"
                style={{ borderColor: errors.dbhdsRegion ? "#dc2626" : profile.dbhdsRegion ? "#D4A843" : "#e5e7eb" }}
              >
                <option value="">Select region</option>
                {DBHDS_REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              {errors.dbhdsRegion && (
                <p className="text-xs mt-1" style={{ color: "#dc2626" }}>{errors.dbhdsRegion}</p>
              )}
            </div>
            <Field label="Total Licensed Bed Count" placeholder="e.g. 6" type="text" inputMode="numeric" value={profile.bedCount} onChange={v => updateProfile("bedCount", filterDigitsInput(v))} maxLength={4} />
          </>
        )}
      </div>
      <GoldButton disabled={!canContinue} onClick={handleContinue} className="mt-6">Continue →</GoldButton>
    </div>
  );
}

/* ─── STEP 3: SERVICE AREA ─── */
function Step3({ segment, profile, updateProfile, toggleArrayItem, zipInput, setZipInput, zipError, addZip, localityInput, setLocalityInput, localityError, addLocality, onNext }: {
  segment: Segment; profile: Profile; updateProfile: (k: keyof Profile, v: any) => void; toggleArrayItem: (k: keyof Profile, v: string) => void;
  zipInput: string; setZipInput: (v: string) => void; zipError: string; addZip: () => void;
  localityInput: string; setLocalityInput: (v: string) => void; localityError: string; addLocality: () => void; onNext: () => void;
}) {
  const items = segment === "homecare" ? profile.serviceZips : profile.localities;
  const warnCount = items.length < 2;

  return (
    <div>
      <h2 className="text-2xl font-bold mb-1" style={{ color: "#1F3864" }}>
        {segment === "homecare" ? "Where do you serve clients?" : "Which localities do you serve?"}
      </h2>
      <p className="text-sm mb-6" style={{ color: "#888" }}>
        {segment === "homecare"
          ? "Add the ZIP codes you serve. Mark your highest-priority areas as Golden Zones — leads from these ZIPs get a scoring bonus."
          : "Add the counties and cities where you accept placements. Mark your primary service localities — inquiries from these areas score higher automatically."}
      </p>

      {segment === "homecare" ? (
        <>
          <div className="flex gap-2 mb-2">
            <input
              value={zipInput}
              onChange={e => { setZipInput(filterZipInput(e.target.value)); }}
              onKeyDown={e => e.key === "Enter" && addZip()}
              placeholder="e.g. 22030"
              maxLength={MAX_LENGTHS.zip}
              inputMode="numeric"
              className="flex-1 px-4 py-3 rounded-lg border text-sm focus:outline-none transition"
              style={{ borderColor: "#e5e7eb" }}
            />
            <button onClick={addZip} className="px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: "#1F3864" }}>Add ZIP</button>
          </div>
          {zipError && <p className="text-xs text-red-500 mb-2">{zipError}</p>}
          <div className="flex flex-wrap gap-2 mb-3">
            {profile.serviceZips.map(z => (
              <span key={z} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border" style={{ borderColor: profile.goldenZips.includes(z) ? "#D4A843" : "#e5e7eb", background: profile.goldenZips.includes(z) ? "#FFFBF0" : "#fff" }}>
                <button onClick={() => toggleArrayItem("goldenZips", z)} className="text-lg leading-none">{profile.goldenZips.includes(z) ? "⭐" : "☆"}</button>
                {z}
                <button onClick={() => updateProfile("serviceZips", profile.serviceZips.filter(x => x !== z))} className="ml-1 text-gray-400 hover:text-red-500">×</button>
              </span>
            ))}
          </div>
          <div className="text-xs space-y-1 mb-4" style={{ color: "#999" }}>
            <p>⭐ Golden Zone — gets a +15 point scoring bonus</p>
            <p>☆ Standard service area — gets a +20 point base match score</p>
          </div>
          <p className="text-xs" style={{ color: "#bbb" }}>Tip: Add at least 3 ZIP codes and mark 1–2 as Golden Zones to activate predictive scoring.</p>
        </>
      ) : (
        <>
          <div className="flex gap-2 mb-2">
            <input
              value={localityInput}
              onChange={e => setLocalityInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && addLocality()}
              placeholder="e.g. Prince William County"
              maxLength={MAX_LENGTHS.locality}
              className="flex-1 px-4 py-3 rounded-lg border text-sm focus:outline-none transition"
              style={{ borderColor: "#e5e7eb" }}
            />
            <button onClick={addLocality} className="px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: "#1F3864" }}>Add</button>
          </div>
          {localityError && <p className="text-xs text-red-500 mb-2">{localityError}</p>}
          <div className="flex flex-wrap gap-2 mb-3">
            {profile.localities.map(l => (
              <span key={l} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border" style={{ borderColor: profile.primaryLocalities.includes(l) ? "#D4A843" : "#e5e7eb", background: profile.primaryLocalities.includes(l) ? "#FFFBF0" : "#fff" }}>
                <button onClick={() => toggleArrayItem("primaryLocalities", l)} className="text-xs font-semibold" style={{ color: profile.primaryLocalities.includes(l) ? "#D4A843" : "#ccc" }}>
                  {profile.primaryLocalities.includes(l) ? "★ Primary" : "Set Primary"}
                </button>
                {l}
                <button onClick={() => updateProfile("localities", profile.localities.filter(x => x !== l))} className="ml-1 text-gray-400 hover:text-red-500">×</button>
              </span>
            ))}
          </div>
          <p className="text-xs" style={{ color: "#bbb" }}>Tip: Add every county or city where your program is licensed to accept placements.</p>
        </>
      )}

      {warnCount && items.length > 0 && (
        <p className="text-xs mt-2" style={{ color: "#D4A843" }}>We recommend adding at least 2 service areas for accurate lead scoring.</p>
      )}
      {items.length === 0 && (
        <p className="text-xs mt-2" style={{ color: "#dc2626" }}>
          {segment === "homecare" ? "Add at least one ZIP code to continue." : "Add at least one locality to continue."}
        </p>
      )}

      <GoldButton disabled={items.length === 0} onClick={onNext} className="mt-6">Continue →</GoldButton>
    </div>
  );
}

/* ─── STEP 4: SPECIALTIES ─── */
function Step4({ segment, profile, toggleArrayItem, onNext }: {
  segment: Segment; profile: Profile; toggleArrayItem: (k: keyof Profile, v: string) => void; onNext: () => void;
}) {
  const specs = segment === "homecare" ? HC_SPECIALTIES : GH_SPECIALTIES;
  const payerLabel = segment === "homecare" ? "Accepted Payer Types" : "Accepted Waiver Types";
  const payerKey: keyof Profile = segment === "homecare" ? "payerTypes" : "waiverTypes";
  const payerOptions = segment === "homecare" ? HC_PAYERS : GH_WAIVERS;
  const payerArr = (segment === "homecare" ? profile.payerTypes : profile.waiverTypes) as string[];

  return (
    <div>
      <h2 className="text-2xl font-bold mb-1" style={{ color: "#1F3864" }}>
        {segment === "homecare" ? "What care do you specialize in?" : "What populations and waiver types do you serve?"}
      </h2>
      <p className="text-sm mb-6" style={{ color: "#888" }}>Select all that apply. Mark your highest-priority services — leads matching these get a scoring boost.</p>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        {specs.map(({ icon: Icon, label }) => {
          const selected = profile.specialties.includes(label);
          const priority = profile.prioritySpecialties.includes(label);
          return (
            <div key={label} className="relative">
              <button
                onClick={() => toggleArrayItem("specialties", label)}
                className="w-full flex flex-col items-center text-center p-4 rounded-xl transition-all"
                style={{
                  border: selected ? "2px solid #D4A843" : "2px solid #e5e7eb",
                  background: selected ? "#FFFBF0" : "#fff",
                }}
              >
                <Icon className="w-6 h-6 mb-2" style={{ color: selected ? "#D4A843" : "#bbb" }} />
                <span className="text-xs font-medium" style={{ color: "#1F3864" }}>{label}</span>
              </button>
              {selected && (
                <button
                  onClick={() => toggleArrayItem("prioritySpecialties", label)}
                  className="mt-1 w-full text-xs py-1 rounded-lg transition"
                  style={{ color: priority ? "#D4A843" : "#bbb", background: priority ? "#FFF8E7" : "transparent" }}
                >
                  {priority ? "⭐ Priority" : "Mark as Priority"}
                </button>
              )}
              {priority && <span className="absolute top-1 right-1 text-xs">⭐</span>}
            </div>
          );
        })}
      </div>

      <h3 className="text-base font-bold mb-3" style={{ color: "#1F3864" }}>{payerLabel}</h3>
      <div className="flex flex-wrap gap-2 mb-4">
        {payerOptions.map(p => {
          const sel = payerArr.includes(p);
          return (
            <button key={p} onClick={() => toggleArrayItem(payerKey, p)}
              className="px-4 py-2 rounded-lg text-sm font-medium transition"
              style={{
                background: sel ? "#D4A843" : "#fff",
                color: sel ? "#1F3864" : "#888",
                border: sel ? "1px solid #D4A843" : "1px solid #e5e7eb",
              }}
            >{p}</button>
          );
        })}
      </div>

      {profile.specialties.length === 0 && (
        <p className="text-xs" style={{ color: "#dc2626" }}>Select at least one specialty to continue.</p>
      )}
      {payerArr.length === 0 && (
        <p className="text-xs" style={{ color: "#dc2626" }}>Select at least one {payerLabel.toLowerCase()} to continue.</p>
      )}

      <GoldButton disabled={profile.specialties.length === 0 || payerArr.length === 0} onClick={onNext} className="mt-4">Continue →</GoldButton>
    </div>
  );
}

/* ─── STEP 5: NOTIFICATIONS ─── */
function Step5({ segment, profile, updateProfile, onNext }: {
  segment: Segment; profile: Profile; updateProfile: (k: keyof Profile, v: any) => void; onNext: () => void;
}) {
  const [errors, setErrors] = useState<Record<string, string>>({});

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (profile.senderName.length > MAX_LENGTHS.senderName) errs.senderName = `Sender Name must be ${MAX_LENGTHS.senderName} characters or fewer.`;
    if (profile.replyToEmail && !isValidEmail(profile.replyToEmail.trim())) errs.replyToEmail = "Enter a valid email address";
    else if (profile.replyToEmail.length > MAX_LENGTHS.replyToEmail) errs.replyToEmail = `Reply-To Email must be ${MAX_LENGTHS.replyToEmail} characters or fewer.`;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleContinue() {
    if (!validate()) return;
    onNext();
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-1" style={{ color: "#1F3864" }}>How should we alert you?</h2>
      <p className="text-sm mb-6" style={{ color: "#888" }}>
        {segment === "homecare"
          ? "When a hot lead comes in, LegacyGrowth Engine can reach you instantly. Configure how and when you want to be notified."
          : "When an urgent placement inquiry comes in, LegacyGrowth Engine can reach you instantly. Configure how and when you want to be notified."}
      </p>

      <div className="space-y-4 mb-6">
        <ToggleRow
          icon={<Smartphone className="w-6 h-6" style={{ color: "#D4A843" }} />}
          title="SMS Text Alerts"
          desc={segment === "homecare"
            ? "Get an instant text when a hot lead (85+) or emergency lead (100) comes in."
            : "Get an instant text when an urgent inquiry (85+) or emergency placement (100) comes in."}
          checked={profile.notifySms}
          onChange={v => updateProfile("notifySms", v)}
        />
        <ToggleRow
          icon={<Mail className="w-6 h-6" style={{ color: "#D4A843" }} />}
          title="Email Alerts"
          desc="Get an email notification for every new lead or inquiry captured by the platform."
          checked={profile.notifyEmail}
          onChange={v => updateProfile("notifyEmail", v)}
        />
      </div>

      <h3 className="text-base font-bold mb-1" style={{ color: "#1F3864" }}>Email Sender Identity</h3>
      <p className="text-xs mb-4" style={{ color: "#999" }}>Outreach emails sent from the platform will appear to come from your agency — not from LegacyGrowth Engine.</p>
      <div className="space-y-3 mb-4">
        <Field label="Sender Name" placeholder={profile.agencyName || "Your agency name"} value={profile.senderName} onChange={v => updateProfile("senderName", v)} maxLength={MAX_LENGTHS.senderName} error={errors.senderName} />
        <Field label="Reply-To Email Address" placeholder={profile.ownerEmail || "your@email.com"} type="email" value={profile.replyToEmail} onChange={v => updateProfile("replyToEmail", v)} maxLength={MAX_LENGTHS.replyToEmail} error={errors.replyToEmail} />
      </div>

      <div className="flex gap-3 p-4 rounded-xl" style={{ background: "#EEF4FB", borderLeft: "4px solid #2E75B6" }}>
        <Info className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "#2E75B6" }} />
        <p className="text-xs" style={{ color: "#444" }}>Your reply-to email is only used when recipients reply to outreach emails. It is never shared publicly or used for marketing.</p>
      </div>

      <GoldButton onClick={handleContinue} className="mt-6">Continue →</GoldButton>
    </div>
  );
}

/* ─── STEP 6: HIPAA ─── */
function Step6(props: {
  ownerName: string;
  agencyName: string;
  scrolledEnd: boolean;
  onScrolledEnd: () => void;
  signerTitle: string;
  setSignerTitle: (v: string) => void;
  signature: SignatureResult | null;
  setSignature: (s: SignatureResult | null) => void;
  attestAuthority: boolean;
  setAttestAuthority: (v: boolean) => void;
  attestEsign: boolean;
  setAttestEsign: (v: boolean) => void;
  signing: boolean;
  onSign: () => void;
}) {
  const {
    ownerName, agencyName, scrolledEnd, onScrolledEnd,
    signerTitle, setSignerTitle, signature, setSignature,
    attestAuthority, setAttestAuthority, attestEsign, setAttestEsign,
    signing, onSign,
  } = props;

  const canSign =
    scrolledEnd && !!signature && attestAuthority && attestEsign && !signing;

  return (
    <div>
      <h2 className="text-2xl font-bold mb-1" style={{ color: "#1F3864" }}>
        One important step before you go live.
      </h2>
      <p className="text-sm mb-5" style={{ color: "#888" }}>
        Because LegacyGrowth Engine handles protected health information on
        your behalf, federal law requires a Business Associate Agreement (BAA)
        between {agencyName || "your organization"} and H&amp;E Associates LLC.
        Review it below and sign electronically — no paperwork required.
      </p>

      <div className="rounded-xl p-4 mb-5 flex items-start gap-3" style={{ background: "#EEF4FB", borderLeft: "4px solid #2E75B6" }}>
        <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "#2E75B6" }} />
        <p className="text-xs" style={{ color: "#444" }}>
          <strong>Electronic signature notice (E-SIGN / UETA):</strong> By signing
          below, you consent to do business electronically and agree your e-signature
          is the legal equivalent of a handwritten signature. You may request a paper
          copy at any time by emailing <a className="underline" href="mailto:compliance@heassociatesllc.com">compliance@heassociatesllc.com</a>.
        </p>
      </div>

      <BAAReviewPanel className="mb-5" onScrolledToEnd={onScrolledEnd} />

      <div className="space-y-3 mb-5">
        <div>
          <label className="text-sm font-semibold block mb-1" style={{ color: "#1F3864" }}>
            Your title (optional)
          </label>
          <input
            value={signerTitle}
            onChange={e => setSignerTitle(e.target.value)}
            placeholder="e.g. Owner, Administrator"
            maxLength={MAX_LENGTHS.signerTitle}
            className="w-full px-4 py-3 rounded-lg border text-sm focus:outline-none"
            style={{ borderColor: "#e5e7eb" }}
          />
          {signerTitle.length >= MAX_LENGTHS.signerTitle && (
            <p className="text-xs mt-1" style={{ color: "#D4A843" }}>
              Title has reached the {MAX_LENGTHS.signerTitle}-character limit.
            </p>
          )}
        </div>

        <div>
          <label className="text-sm font-semibold block mb-2" style={{ color: "#1F3864" }}>
            Signature
          </label>
          <BAASignaturePad signerName={ownerName} onChange={setSignature} />
        </div>
      </div>

      <label className="flex items-start gap-3 cursor-pointer mb-2">
        <input
          type="checkbox"
          checked={attestAuthority}
          onChange={e => setAttestAuthority(e.target.checked)}
          className="mt-1 w-5 h-5 accent-yellow-500 shrink-0"
        />
        <span className="text-sm" style={{ color: "#333" }}>
          I am authorized to bind <strong>{agencyName || "my organization"}</strong> to this Business Associate Agreement.
        </span>
      </label>
      <label className="flex items-start gap-3 cursor-pointer mb-5">
        <input
          type="checkbox"
          checked={attestEsign}
          onChange={e => setAttestEsign(e.target.checked)}
          className="mt-1 w-5 h-5 accent-yellow-500 shrink-0"
        />
        <span className="text-sm" style={{ color: "#333" }}>
          I agree this electronic signature is the legal equivalent of my handwritten signature under the E-SIGN Act and UETA.
        </span>
      </label>

      <GoldButton disabled={!canSign} onClick={onSign}>
        {signing ? "Signing…" : "Sign & Complete Setup →"}
      </GoldButton>
      {!scrolledEnd && (
        <p className="text-xs mt-2 text-center" style={{ color: "#aaa" }}>
          Scroll through the document above to enable signing.
        </p>
      )}
    </div>
  );
}

/* ─── STEP 7: COMPLETION ─── */
function Step7({ saving, done, segment, profile, revealLines, onComplete }: {
  saving: boolean; done: boolean; segment: Segment | null; profile: Profile; revealLines: number[]; onComplete: () => void;
}) {
  if (!done) {
    return (
      <div className="text-center py-12">
        <Loader2 className="w-10 h-10 mx-auto mb-4 animate-spin" style={{ color: "#D4A843" }} />
        <p className="text-base mb-4" style={{ color: "#888" }}>Setting up your personalized dashboard...</p>
        <div className="space-y-2 text-sm" style={{ color: "#aaa" }}>
          {revealLines.includes(1) && <p className="animate-[slideInRight_280ms_ease-out]">Configuring your lead scoring engine...</p>}
          {revealLines.includes(2) && <p className="animate-[slideInRight_280ms_ease-out]">Loading your email templates...</p>}
          {revealLines.includes(3) && <p className="animate-[slideInRight_280ms_ease-out]">Activating your AI intake coordinator...</p>}
        </div>
      </div>
    );
  }

  const zipCount = profile.serviceZips.length;
  const goldenCount = profile.goldenZips.length;
  const localityCount = profile.localities.length;
  const specCount = profile.specialties.length;
  const prioCount = profile.prioritySpecialties.length;
  const waiverCount = profile.waiverTypes.length;

  return (
    <div className="text-center animate-[slideInRight_400ms_ease-out]">
      <CheckCircle2 className="w-14 h-14 mx-auto mb-4" style={{ color: "#D4A843", filter: "drop-shadow(0 0 12px rgba(212,168,67,0.3))" }} />
      <h2 className="text-3xl font-bold mb-3" style={{ color: "#1F3864" }}>You're all set. Let's grow.</h2>
      <p className="text-sm mb-8 mx-auto max-w-[500px]" style={{ color: "#888" }}>
        {segment === "homecare"
          ? "Your LegacyGrowth Engine is configured and ready. Your AI care coordinator is live, your scoring engine is calibrated to your service area, and your referral outreach templates are personalized for your agency."
          : "Your LegacyGrowth Engine is configured and ready. Your AI intake coordinator is live, your scoring engine is calibrated to your localities and waiver types, and your referral outreach templates are personalized for your program."}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
        <SummaryCard icon={<MapPin className="w-5 h-5" style={{ color: "#D4A843" }} />} title={segment === "homecare" ? "Service Area Configured" : "Localities Configured"}
          desc={segment === "homecare" ? `${zipCount} ZIP codes added, ${goldenCount} Golden Zones marked` : `${localityCount} localities added`} />
        <SummaryCard icon={<Star className="w-5 h-5" style={{ color: "#D4A843" }} />} title={segment === "homecare" ? "Specialties Set" : "Populations Set"}
          desc={segment === "homecare" ? `${specCount} specialties active, ${prioCount} marked Priority` : `${specCount} specialties active, ${waiverCount} waiver types enabled`} />
        <SummaryCard icon={<Bell className="w-5 h-5" style={{ color: "#D4A843" }} />} title="Alerts Active"
          desc={`${[profile.notifySms && "SMS", profile.notifyEmail && "Email"].filter(Boolean).join(" and ")} notifications are on`} />
      </div>

      <GoldButton onClick={onComplete} className="max-w-[400px] mx-auto">Go to My Dashboard →</GoldButton>
      <div className="flex justify-center gap-3 mt-4 text-xs" style={{ color: "#999" }}>
        <a href="/settings" className="hover:underline">Review your settings</a>
        <span>·</span>
        <a href="#" target="_blank" rel="noopener noreferrer" className="hover:underline">Sign your BAA</a>
      </div>
    </div>
  );
}

function SummaryCard({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <div className="flex items-start gap-3 p-4 rounded-xl border text-left" style={{ borderColor: "#e5e7eb" }}>
      {icon}
      <div>
        <p className="text-sm font-bold" style={{ color: "#1F3864" }}>{title}</p>
        <p className="text-xs" style={{ color: "#888" }}>{desc}</p>
      </div>
    </div>
  );
}

/* ─── SHARED COMPONENTS ─── */
function Field({ label, placeholder, value, onChange, type = "text", inputMode, maxLength, error }: {
  label: string; placeholder: string; value: string; onChange: (v: string) => void; type?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"]; maxLength?: number; error?: string;
}) {
  const atLimit = !!maxLength && value.length >= maxLength;
  return (
    <div>
      <label className="text-sm font-semibold block mb-1" style={{ color: "#1F3864" }}>{label}</label>
      <input
        type={type}
        inputMode={inputMode}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className="w-full px-4 py-3 rounded-lg border text-sm transition focus:outline-none"
        style={{ borderColor: error ? "#dc2626" : "#e5e7eb" }}
      />
      {error ? (
        <p className="text-xs mt-1" style={{ color: "#dc2626" }}>{error}</p>
      ) : atLimit ? (
        <p className="text-xs mt-1" style={{ color: "#D4A843" }}>
          {label} has reached the {maxLength}-character limit.
        </p>
      ) : null}
    </div>
  );
}

function ToggleRow({ icon, title, desc, checked, onChange }: {
  icon: React.ReactNode; title: string; desc: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between p-5 rounded-xl border" style={{ borderColor: "#e5e7eb" }}>
      <div className="flex items-start gap-3">
        {icon}
        <div>
          <p className="text-sm font-bold" style={{ color: "#1F3864" }}>{title}</p>
          <p className="text-xs" style={{ color: "#888" }}>{desc}</p>
        </div>
      </div>
      <button
        onClick={() => onChange(!checked)}
        className="w-12 h-6 rounded-full transition-colors relative shrink-0 ml-3"
        style={{ background: checked ? "#D4A843" : "#ddd" }}
      >
        <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform" style={{ left: checked ? "26px" : "2px" }} />
      </button>
    </div>
  );
}

function GoldButton({ children, onClick, disabled, className = "" }: {
  children: React.ReactNode; onClick: () => void; disabled?: boolean; className?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full py-3.5 rounded-lg text-sm font-bold transition ${className}`}
      style={{
        background: disabled ? "#ddd" : "#D4A843",
        color: disabled ? "#999" : "#1F3864",
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >{children}</button>
  );
}
